import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';
import { evaluateSchoolSubscription } from './subscription';
import { clearPersistedLogin } from './persistedAuth';

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getSessionUser() {
  return safeJsonParse(sessionStorage.getItem('currentUser'));
}

function clearSessionEverywhere() {
  try { sessionStorage.removeItem('currentUser'); } catch {}
  try { sessionStorage.removeItem('pendingLogin'); } catch {}
  try { clearPersistedLogin(); } catch {}
}

/**
 * Realtime guard:
 * - If current user's `users.is_active` becomes false => block + redirect to "/"
 * - If their school `schools.subscription_paid` / subscription status becomes inaccessible => block + redirect to "/"
 *
 * This is meant to be used on in-app pages (ProviderModal/Calendar/Profile).
 */
export function useRealtimeAccessGuard({
  userId,
  schoolId,
  enabled = true,
  redirectPath = '/',
  autoRedirect = false,
  redirectDelayMs = 1200,
}) {
  const [blockInfo, setBlockInfo] = useState(null);
  const didBlockRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const block = (reason, title, message) => {
    if (didBlockRef.current) return;
    didBlockRef.current = true;
    setBlockInfo({ reason, title, message });

    // Clear session immediately to prevent further activity.
    clearSessionEverywhere();

    // Optional auto redirect.
    if (autoRedirect) {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = setTimeout(() => {
        window.location.href = redirectPath;
      }, Math.max(0, Number(redirectDelayMs) || 0));
    }
  };

  const acknowledge = () => {
    // Ensure session is cleared even if caller invokes acknowledge without a prior block().
    clearSessionEverywhere();
    window.location.href = redirectPath;
  };

  useEffect(() => {
    if (!enabled) return undefined;
    if (!userId) return undefined;

    let cancelled = false;
    let userChannel = null;
    let schoolChannel = null;

    async function initialCheck() {
      try {
        // 1) User status check
        const { data: userRow, error: userErr } = await supabase
          .from('users')
          .select('id, is_active, school_id')
          .eq('id', userId)
          .maybeSingle();

        if (!cancelled && userErr && userErr.code !== 'PGRST116') {
          // If we can't verify, don't hard-block.
          // (RLS/network/schema mismatch)
        }

        if (!cancelled && userRow && userRow.is_active === false) {
          block(
            'user_inactive',
            'Account inactive',
            'Your account has been deactivated. You will be redirected to the home page.'
          );
          return;
        }

        // 2) School subscription check (if we have a school id)
        const effectiveSchoolId = schoolId || userRow?.school_id || getSessionUser()?.school_id || null;
        if (!effectiveSchoolId) return;

        const { data: school, error: schoolErr } = await supabase
          .from('schools')
          .select('id, subscription_start_date, subscription_end_date, subscription_paid, subscription_warning_days')
          .eq('id', effectiveSchoolId)
          .maybeSingle();

        if (!cancelled && schoolErr && schoolErr.code !== 'PGRST116') {
          // Can't verify => do not hard-block
          return;
        }

        if (!cancelled && school) {
          const sub = evaluateSchoolSubscription(school);
          if (!sub.isAccessible) {
            block(
              'school_inactive',
              'School subscription inactive',
              sub.message || 'Account Currently Unavailable'
            );
          }
        }
      } catch {
        // ignore
      }
    }

    initialCheck();

    // Realtime: watch ONLY this user row.
    userChannel = supabase
      .channel(`mds-guard-user-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          if (cancelled) return;
          if (payload.eventType === 'DELETE') {
            block(
              'user_deleted',
              'Account removed',
              'Your account is no longer available. You will be redirected to the home page.'
            );
            return;
          }

          const next = payload.new || null;
          if (next && next.is_active === false) {
            block(
              'user_inactive',
              'Account inactive',
              'Your account has been deactivated. You will be redirected to the home page.'
            );
          }
        }
      )
      .subscribe();

    const effectiveSchoolId = schoolId || getSessionUser()?.school_id || null;
    if (effectiveSchoolId) {
      schoolChannel = supabase
        .channel(`mds-guard-school-${effectiveSchoolId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'schools',
            filter: `id=eq.${effectiveSchoolId}`,
          },
          (payload) => {
            if (cancelled) return;
            if (payload.eventType === 'DELETE') {
              block(
                'school_deleted',
                'School unavailable',
                'This school is no longer available. You will be redirected to the home page.'
              );
              return;
            }

            const school = payload.new || null;
            if (!school) return;
            const sub = evaluateSchoolSubscription(school);
            if (!sub.isAccessible) {
              block(
                'school_inactive',
                'School subscription inactive',
                sub.message || 'Account Currently Unavailable'
              );
            }
          }
        )
        .subscribe();
    }

    return () => {
      cancelled = true;
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      if (userChannel) supabase.removeChannel(userChannel);
      if (schoolChannel) supabase.removeChannel(schoolChannel);
    };
  }, [enabled, userId, schoolId, redirectPath, autoRedirect, redirectDelayMs]);

  return { blockInfo, acknowledge };
}


